import React from "react";
import { Link } from "react-router-dom";
import useScrollReveal from "../../hooks/useScrollReveal";
import LabImage from "../../images/DelNovaHomeImage.jpg";

export default function AboutSection() {
  const [ref, isVisible] = useScrollReveal();

  return (
    <section
      ref={ref}
      id="about"
      className={`py-24 bg-[#F7F9FA] transition-all duration-1000 transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
        }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          {/* Left Column: Image and Highlight */}
          <div className="lg:col-span-5 relative">
            <div className="absolute -top-4 -left-4 w-72 h-72 bg-brand-teal/5 rounded-3xl blur-3xl pointer-events-none" />
            <div className="absolute -bottom-4 -right-4 w-72 h-72 bg-brand-blue/5 rounded-3xl blur-3xl pointer-events-none" />

            <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-white/80">
              <img
                src={LabImage}
                alt="High-tech lab automation equipment"
                className="w-full h-[450px] object-cover transition-transform duration-700 hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-blue/40 to-transparent pointer-events-none" />

              {/* Highlight Card Overlay */}
              <div className="absolute bottom-6 left-6 right-6 glass-card p-6 rounded-xl border border-white/60">
                <p className="text-brand-blue font-bold text-lg mb-1">DelNova Quality</p>
                <p className="text-slate-600 text-xs leading-relaxed">
                  Integrating state-of-the-art automation and advanced pharmaceutical processes.
                </p>
              </div>
            </div>
          </div>

          {/* Right Column: Mission Content */}
          <div className="lg:col-span-7 space-y-8">
            <div className="space-y-3">
              <span className="text-brand-teal font-bold text-xs uppercase tracking-widest block">
                Who We Are
              </span>
              <h2 className="text-3xl md:text-4xl font-extrabold text-brand-blue tracking-tight">
                We Are Different
              </h2>
              <div className="h-1 w-20 bg-brand-teal rounded-full" />
            </div>

            <p className="text-slate-700 text-base md:text-lg leading-relaxed font-light">
              We are a research-driven, innovative pharmaceutical company focused on{" "}
              <strong className="font-semibold text-brand-blue">
                “Reducing the burden of Healthcare”
              </strong>{" "}
              through alliances in development and manufacturing.
            </p>

            <div className="space-y-6">
              {/* Point 1 */}
              <div className="flex items-start space-x-4">
                <div className="mt-1 flex-shrink-0 w-6 h-6 rounded-full bg-brand-teal/10 flex items-center justify-center text-brand-teal">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-bold text-brand-blue text-base md:text-lg mb-1">
                    Reducing Healthcare Cost
                  </h3>
                  <p className="text-slate-600 text-sm md:text-base leading-relaxed">
                    Providing simple, robust solutions to the ever-challenging problems faced by generic and innovator companies globally.
                  </p>
                </div>
              </div>

              {/* Point 2 */}
              <div className="flex items-start space-x-4">
                <div className="mt-1 flex-shrink-0 w-6 h-6 rounded-full bg-brand-teal/10 flex items-center justify-center text-brand-teal">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-bold text-brand-blue text-base md:text-lg mb-1">
                    Robust and Bioequivalent Compositions
                  </h3>
                  <p className="text-slate-600 text-sm md:text-base leading-relaxed">
                    Helping partners to develop stable, reproducible processes producing bioequivalent formulations that meets regulatory checkmarks.
                  </p>
                </div>
              </div>

              {/* Point 3 */}
              <div className="flex items-start space-x-4">
                <div className="mt-1 flex-shrink-0 w-6 h-6 rounded-full bg-brand-teal/10 flex items-center justify-center text-brand-teal">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-bold text-brand-blue text-base md:text-lg mb-1">
                    Alliance and Development Partnering
                  </h3>
                  <p className="text-slate-600 text-sm md:text-base leading-relaxed">
                    Acting as an “Extended R and D Partner”, tracking project cost transparently and ensuring on-time delivery of the product to meet business needs.
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-4">
              <Link
                to="/AboutUs"
                className="inline-flex items-center text-brand-teal font-semibold text-sm hover:text-brand-blue transition-colors duration-200"
              >
                Learn more about our organization
                <svg className="w-4 h-4 ml-2 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
